import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, apiFetch } from '../config/api';
import { batteryVerdict, estimateDelivery } from '../utils/deliveryEstimate';

const MAX_LOCATIONS = 10;

// ─── Material icon helper ───
const Icon = ({ name, size = 20, fill = false, style = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: size,
      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${size}`,
      lineHeight: 1,
      ...style,
    }}
  >
    {name}
  </span>
);

function DeliveryDialog({ isOpen, onClose, selectedDrone, droneConnected, onDeliveryCreated, deliveryCoords, dronePos }) {
  const [locations, setLocations] = useState([{ lat: '', lon: '', name: '' }]);
  const [altitude, setAltitude] = useState('50');
  const [navSpeed, setNavSpeed] = useState('5');
  const [dropHeight, setDropHeight] = useState('15');
  const [missionName, setMissionName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [servoChannel, setServoChannel] = useState('9');
  const [servoPWM, setServoPWM] = useState('1100');
  const [loiterTime, setLoiterTime] = useState('5');
  const [ropeLength, setRopeLength] = useState('15');
  const [winchRate, setWinchRate] = useState('0.5');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [savedLocations, setSavedLocations] = useState([]);

  // Saved delivery locations (Deliveries page address book) so a stop can be
  // picked instead of retyped. Manual entry stays for ad-hoc drops.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    apiFetch(`${API_URL}/delivery/locations`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (!cancelled && data?.locations) setSavedLocations(data.locations.filter(l => l.active)); })
      .catch(() => { /* the dialog still works with manual coordinates */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Append coordinates from map click as a new location
  useEffect(() => {
    if (deliveryCoords) {
      setLocations(prev => {
        const last = prev[prev.length - 1];
        if (last && !last.lat && !last.lon) {
          const updated = [...prev];
          updated[prev.length - 1] = {
            lat: deliveryCoords.lat.toFixed(7),
            lon: deliveryCoords.lng.toFixed(7),
            name: last.name || '',
          };
          return updated;
        }
        if (prev.length >= MAX_LOCATIONS) return prev;
        return [...prev, {
          lat: deliveryCoords.lat.toFixed(7),
          lon: deliveryCoords.lng.toFixed(7),
          name: '',
        }];
      });
      setError('');
    }
  }, [deliveryCoords]);

  const resetForm = useCallback(() => {
    setLocations([{ lat: '', lon: '', name: '' }]);
    setAltitude('50');
    setNavSpeed('5');
    setDropHeight('15');
    setMissionName('');
    setServoChannel('9');
    setServoPWM('1100');
    setLoiterTime('5');
    setRopeLength('15');
    setWinchRate('0.5');
    setShowAdvanced(false);
    setError('');
    setStatus('');
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, handleClose]);

  if (!isOpen) return null;

  const updateLocation = (index, field, value) => {
    setLocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setError('');
  };

  const addLocation = () => {
    if (locations.length >= MAX_LOCATIONS) return;
    setLocations(prev => [...prev, { lat: '', lon: '', name: '' }]);
  };

  // Fill the first blank row with a saved location, else append a new stop.
  // Same rule the map-click handler uses, so both entry paths behave alike.
  const insertSavedLocation = (saved) => {
    if (!saved) return;
    const row = { lat: String(saved.lat), lon: String(saved.lon), name: saved.name };
    setLocations(prev => {
      const blankIndex = prev.findIndex(loc => !loc.lat && !loc.lon);
      if (blankIndex >= 0) {
        const updated = [...prev];
        updated[blankIndex] = row;
        return updated;
      }
      if (prev.length >= MAX_LOCATIONS) return prev;
      return [...prev, row];
    });
    setError('');
  };

  const removeLocation = (index) => {
    if (locations.length <= 1) return;
    setLocations(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const alt = parseFloat(altitude);
    const speed = parseFloat(navSpeed);
    const drop = parseFloat(dropHeight);
    const rope = parseFloat(ropeLength);
    const rate = parseFloat(winchRate);
    const ch = parseInt(servoChannel, 10);
    const pwm = parseInt(servoPWM, 10);
    const lt = parseInt(loiterTime, 10);

    if (locations.length === 0) return 'At least one delivery location is required';

    for (let i = 0; i < locations.length; i++) {
      const loc = locations[i];
      const lat = parseFloat(loc.lat);
      const lon = parseFloat(loc.lon);
      if (!loc.lat || !loc.lon) return `Location #${i + 1}: Latitude and longitude are required`;
      if (isNaN(lat) || lat < -90 || lat > 90) return `Location #${i + 1}: Latitude must be between -90 and 90`;
      if (isNaN(lon) || lon < -180 || lon > 180) return `Location #${i + 1}: Longitude must be between -180 and 180`;
    }

    if (isNaN(alt) || alt <= 0 || alt > 500) return 'Altitude must be between 1 and 500 meters';
    if (isNaN(speed) || speed <= 0 || speed > 20) return 'Speed must be between 0.1 and 20 m/s';
    if (isNaN(drop) || drop <= 0 || drop >= alt) return 'Drop height must be > 0 and below cruise altitude';
    if (isNaN(rope) || rope <= 0 || rope > 100) return 'Rope length must be between 1 and 100 meters';
    if (isNaN(rate) || rate <= 0 || rate > 5) return 'Winch rate must be between 0.1 and 5 m/s';
    if (isNaN(ch) || ch < 1 || ch > 16) return 'Servo channel must be between 1 and 16';
    if (isNaN(pwm) || pwm < 800 || pwm > 2200) return 'Servo PWM must be between 800 and 2200';
    if (isNaN(lt) || lt < 1 || lt > 60) return 'Loiter time must be between 1 and 60 seconds';
    return null;
  };

  const handleGenerate = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    if (!selectedDrone) { setError('No drone selected'); return; }
    if (!droneConnected) { setError('Drone is not connected'); return; }

    setError('');
    setLoading(true);
    setStatus('creating');

    try {
      setStatus('uploading');

      const res = await apiFetch(`${API_URL}/drones/${selectedDrone}/delivery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          missionName: missionName || undefined,
          locations: locations.map(loc => ({
            lat: parseFloat(loc.lat),
            lon: parseFloat(loc.lon),
            name: loc.name || undefined,
          })),
          altitude: parseFloat(altitude) || 50,
          navSpeed: parseFloat(navSpeed) || 5,
          dropHeight: parseFloat(dropHeight) || 15,
          servoChannel: parseInt(servoChannel, 10) || 9,
          servoPWM: parseInt(servoPWM, 10) || 1100,
          loiterTime: parseInt(loiterTime, 10) || 5,
          ropeLength: parseFloat(ropeLength) || 15,
          winchRate: parseFloat(winchRate) || 0.5,
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = `HTTP ${res.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch { /* use default */ }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to create delivery mission');
      }

      setStatus('done');
      if (onDeliveryCreated) onDeliveryCreated(data.mission);
      // Keep the dialog open long enough to read why the route changed.
      const dc = data.deconfliction;
      if (dc) {
        setError(dc.adjusted ? dc.summary : dc.message);
      }
      setTimeout(() => handleClose(), dc ? 6000 : 1500);
    } catch (err) {
      console.error('Delivery mission error:', err);
      setError(err.message || 'Failed to create delivery mission');
      setStatus('error');
      setLoading(false);
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'creating': return 'Creating delivery mission…';
      case 'uploading': return `Generating & uploading ${locations.length} stop${locations.length > 1 ? 's' : ''} to drone…`;
      case 'done': return 'Delivery mission ready! Click Start Mission to begin.';
      default: return '';
    }
  };

  const validLocations = locations.filter(loc => loc.lat && loc.lon);

  // Real round-trip length: drone's current position -> every stop in order ->
  // back home. The old figure was `stops * 2.5 + 1.2` km, a constant that never
  // looked at the coordinates at all, so a 100 m drop and a 40 km drop both read
  // "3.7 km". Without a drone position there is no start point, so show "--"
  // rather than invent one.
  const startPos = dronePos && Number.isFinite(Number(dronePos.lat)) && Number.isFinite(Number(dronePos.lon))
    ? { lat: Number(dronePos.lat), lon: Number(dronePos.lon) }
    : null;
  const est = estimateDelivery({
    start: startPos,
    stops: validLocations.map(loc => ({ lat: parseFloat(loc.lat), lon: parseFloat(loc.lon) })),
    navSpeed,
    winch: { ropeLength, winchRate, settleHold: loiterTime },
  });
  const estDistance = est ? est.km.toFixed(2) : null;
  const estBattery = est ? est.batteryPercent : 0;
  const estBatteryVerdict = batteryVerdict(est, dronePos?.battery_remaining);

  return (
    <>
      <style>{`
        /* ── Delivery Dialog Scoped Styles ── */
        .dd-overlay {
          position: fixed; inset: 0;
          background: rgba(0, 0, 0, 0.55);
          display: flex; align-items: center; justify-content: center;
          z-index: 10001;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          animation: dd-fadeIn 0.25s ease;
        }

        .dd-panel {
          width: 95%; max-width: 820px; max-height: 85vh;
          display: flex; flex-direction: column; overflow: hidden;
          border-radius: 12px;
          background: color-mix(in srgb, var(--bgPrimary) 82%, transparent);
          backdrop-filter: blur(24px) saturate(1.6);
          -webkit-backdrop-filter: blur(24px) saturate(1.6);
          border: 1px solid color-mix(in srgb, var(--accentSecondary) 20%, transparent);
          box-shadow: 0 0 60px color-mix(in srgb, var(--accentSecondary) 8%, transparent), 0 8px 32px rgba(0, 0, 0, 0.45);
          animation: dd-scaleIn 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        /* ── Header ── */
        .dd-header {
          padding: 24px 32px;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent);
        }
        .dd-header-title {
          margin: 0; font-size: 24px; font-weight: 600;
          font-family: 'Hanken Grotesk', sans-serif;
          color: var(--textPrimary); letter-spacing: -0.01em;
        }
        .dd-header-sub {
          margin: 4px 0 0; font-size: 12px;
          font-family: 'JetBrains Mono', monospace; font-weight: 500;
          color: color-mix(in srgb, var(--textSecondary) 70%, transparent); letter-spacing: 0.05em;
        }
        .dd-close-btn {
          width: 40px; height: 40px; border-radius: 50%;
          border: none; background: transparent; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: var(--textSecondary); transition: background 0.15s, color 0.15s;
        }
        .dd-close-btn:hover { background: color-mix(in srgb, var(--bgTertiary) 30%, transparent); color: var(--textPrimary); }

        /* ── Scrollable Body ── */
        .dd-body {
          flex: 1; overflow-y: auto; padding: 32px;
          display: flex; flex-direction: column; gap: 28px;
        }
        .dd-body::-webkit-scrollbar { width: 6px; }
        .dd-body::-webkit-scrollbar-track { background: transparent; }
        .dd-body::-webkit-scrollbar-thumb {
          background: color-mix(in srgb, var(--accent) 25%, transparent); border-radius: 3px;
        }
        .dd-body::-webkit-scrollbar-thumb:hover {
          background: color-mix(in srgb, var(--accent) 45%, transparent);
        }

        /* ── Section Label ── */
        .dd-section-label {
          display: block; margin-bottom: 8px;
          font-size: 10px; font-family: 'JetBrains Mono', monospace;
          font-weight: 500; text-transform: uppercase;
          letter-spacing: 0.08em; color: var(--textSecondary);
        }

        /* ── Underline Input ── */
        .dd-underline-input {
          width: 100%; border: none;
          border-bottom: 2px solid var(--borderSecondary);
          background: rgba(13, 14, 15, 0.5);
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px; font-weight: 500;
          letter-spacing: 0.05em;
          padding: 10px 12px; color: var(--accent);
          outline: none; transition: border-color 0.2s;
          box-sizing: border-box;
        }
        .dd-underline-input::placeholder { color: color-mix(in srgb, var(--textMuted) 50%, transparent); }
        .dd-underline-input:focus { border-bottom-color: var(--accent); }

        /* ── Coordinates Table ── */
        .dd-table-wrap {
          overflow-x: auto; border-radius: 8px;
          border: 1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent);
          background: rgba(0, 0, 0, 0.2);
        }
        .dd-table {
          width: 100%; text-align: left; border-collapse: collapse;
        }
        .dd-table th {
          padding: 10px 12px; font-size: 10px;
          font-family: 'JetBrains Mono', monospace; font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--textSecondary); background: color-mix(in srgb, var(--bgTertiary) 40%, transparent);
          border-bottom: 1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent);
        }
        .dd-table td {
          padding: 10px 12px; font-size: 12px;
          font-family: 'JetBrains Mono', monospace; font-weight: 500;
          letter-spacing: 0.05em; color: var(--accent);
          border-bottom: 1px solid color-mix(in srgb, var(--borderSecondary) 10%, transparent);
        }
        .dd-table td.dd-td-name { color: var(--textPrimary); }
        .dd-table tr { transition: background 0.15s; }
        .dd-table tbody tr:hover { background: color-mix(in srgb, var(--accent) 5%, transparent); }
        .dd-cell-input {
          background: transparent; border: none; outline: none;
          font-family: 'JetBrains Mono', monospace; font-size: 12px;
          font-weight: 500; letter-spacing: 0.05em;
          color: inherit; width: 100%; padding: 0;
        }
        .dd-cell-input::placeholder { color: color-mix(in srgb, var(--textMuted) 40%, transparent); }

        /* ── Add btn inside table ── */
        .dd-add-icon-btn {
          background: none; border: none; padding: 4px;
          cursor: pointer; color: color-mix(in srgb, var(--accent) 50%, transparent);
          display: flex; align-items: center; justify-content: center;
          transition: color 0.15s;
        }
        .dd-add-icon-btn:hover { color: var(--accentSecondary); }
        .dd-add-icon-btn:disabled { color: color-mix(in srgb, var(--textMuted) 30%, transparent); cursor: not-allowed; }

        /* ── Delete button ── */
        .dd-delete-btn {
          background: none; border: none; padding: 4px;
          cursor: pointer; color: rgba(255, 180, 171, 0.6);
          display: flex; align-items: center; justify-content: center;
          transition: color 0.15s;
        }
        .dd-delete-btn:hover { color: #ffb4ab; }

        /* ── Delivery Sequence Card ── */
        .dd-seq-card {
          display: flex; align-items: center; gap: 16px;
          padding: 14px 16px; border-radius: 8px;
          background: color-mix(in srgb, var(--bgSecondary) 50%, transparent);
          border: 1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent);
          transition: border-color 0.2s, background 0.2s;
          cursor: default;
        }
        .dd-seq-card:hover { border-color: color-mix(in srgb, var(--accent) 50%, transparent); }
        .dd-seq-badge {
          width: 32px; height: 32px; border-radius: 50%;
          background: color-mix(in srgb, var(--accent) 10%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 20%, transparent);
          display: flex; align-items: center; justify-content: center;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px; font-weight: 500; color: var(--accent);
          flex-shrink: 0;
        }
        .dd-seq-name {
          margin: 0; font-size: 12px;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 500; letter-spacing: 0.05em; color: var(--textPrimary);
        }
        .dd-seq-meta {
          margin: 2px 0 0; font-size: 10px;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 500; letter-spacing: 0.08em; color: var(--textSecondary);
        }
        .dd-drag-handle {
          color: color-mix(in srgb, var(--textMuted) 50%, transparent); cursor: grab;
          display: flex; align-items: center;
        }

        /* ── Stats Grid ── */
        .dd-stats-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
        }
        .dd-stat-card {
          padding: 16px; border-radius: 8px;
          background: color-mix(in srgb, var(--bgSecondary) 50%, transparent);
          border: 1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent);
        }
        .dd-stat-label {
          margin: 0 0 4px; font-size: 10px;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 500; text-transform: uppercase;
          letter-spacing: 0.08em; color: var(--textSecondary);
        }
        .dd-stat-value {
          margin: 0; font-size: 20px; font-weight: 600;
          font-family: 'Hanken Grotesk', sans-serif; color: var(--accent);
        }

        /* ── Alert Bar ── */
        .dd-alert {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px; border-radius: 8px;
        }
        .dd-alert-warning {
          background: rgba(147, 0, 10, 0.2);
          border: 1px solid rgba(255, 180, 171, 0.3);
        }
        .dd-alert-warning .dd-alert-icon { color: #ffb4ab; }
        .dd-alert-warning .dd-alert-text { color: rgba(255, 180, 171, 0.6); font-size: 14px; font-family: 'Hanken Grotesk', sans-serif; margin: 0; }
        .dd-alert-info {
          background: color-mix(in srgb, var(--accent) 8%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 20%, transparent);
        }
        .dd-alert-info .dd-alert-text { color: var(--accent); font-size: 14px; font-family: 'Hanken Grotesk', sans-serif; margin: 0; }
        .dd-alert-success {
          background: color-mix(in srgb, var(--success) 15%, transparent);
          border: 1px solid color-mix(in srgb, var(--success) 30%, transparent);
        }
        .dd-alert-success .dd-alert-icon { color: var(--success); }
        .dd-alert-success .dd-alert-text { color: var(--success); font-size: 14px; font-family: 'Hanken Grotesk', sans-serif; margin: 0; }

        /* ── Footer ── */
        .dd-footer {
          padding: 20px 32px;
          background: rgba(41, 42, 43, 0.5);
          border-top: 1px solid color-mix(in srgb, var(--borderSecondary) 30%, transparent);
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap;
        }
        .dd-footer-info {
          display: flex; align-items: center; gap: 8px;
          font-size: 10px; font-family: 'JetBrains Mono', monospace;
          font-weight: 500; letter-spacing: 0.08em; color: var(--textSecondary);
        }
        .dd-cancel-btn {
          padding: 10px 24px; border: none; border-radius: 2px;
          background: transparent; cursor: pointer;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px; font-weight: 500; letter-spacing: 0.05em;
          color: var(--textSecondary); transition: background 0.15s, color 0.15s;
        }
        .dd-cancel-btn:hover { background: color-mix(in srgb, var(--bgTertiary) 40%, transparent); color: var(--textPrimary); }
        .dd-launch-btn {
          padding: 10px 32px; border: none; border-radius: 2px;
          background: var(--accentSecondary); color: #00363d; cursor: pointer;
          font-family: 'Hanken Grotesk', sans-serif;
          font-size: 14px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.08em;
          display: flex; align-items: center; gap: 10px;
          transition: filter 0.15s, transform 0.1s;
          box-shadow: 0 0 20px color-mix(in srgb, var(--accentSecondary) 40%, transparent);
        }
        .dd-launch-btn:hover { filter: brightness(1.1); }
        .dd-launch-btn:active { transform: scale(0.95); }
        .dd-launch-btn:disabled {
          background: var(--borderSecondary); color: var(--textMuted);
          cursor: not-allowed; box-shadow: none; opacity: 0.7;
        }
        .dd-launch-btn:disabled:hover { filter: none; }

        /* ── Advanced Settings ── */
        .dd-adv-toggle {
          background: none; border: none; padding: 2px 0; cursor: pointer;
          display: flex; align-items: center; gap: 6px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px; font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.08em;
          color: var(--accent); transition: color 0.15s;
        }
        .dd-adv-toggle:hover { color: var(--accentSecondary); }
        .dd-adv-panel {
          margin-top: 8px; padding: 16px; border-radius: 8px;
          background: rgba(13, 14, 15, 0.5);
          border: 1px solid color-mix(in srgb, var(--borderSecondary) 20%, transparent);
        }
        .dd-adv-label {
          display: block; margin-bottom: 6px; font-size: 10px;
          font-family: 'JetBrains Mono', monospace; font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.08em; color: var(--textSecondary);
        }
        .dd-adv-input {
          width: 100%; border: none;
          border-bottom: 2px solid var(--borderSecondary);
          background: transparent;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px; font-weight: 500;
          letter-spacing: 0.05em;
          padding: 8px 0; color: var(--accent);
          outline: none; box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .dd-adv-input:focus { border-bottom-color: var(--accent); }
        .dd-adv-input::placeholder { color: color-mix(in srgb, var(--textMuted) 50%, transparent); }

        /* ── Drone Warning Bar ── */
        .dd-drone-warning {
          padding: 8px 32px; text-align: center;
          font-size: 10px; font-family: 'JetBrains Mono', monospace;
          font-weight: 500; letter-spacing: 0.08em;
          color: var(--warning); background: color-mix(in srgb, var(--warning) 8%, transparent);
          border-top: 1px solid color-mix(in srgb, var(--warning) 20%, transparent);
        }

        /* ── Spinner ── */
        .dd-spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid var(--accent); border-top-color: transparent;
          border-radius: 50%; animation: dd-spin 1s linear infinite;
          flex-shrink: 0;
        }

        /* ── Add location dashed btn ── */
        .dd-add-row-btn {
          background: none; border: 1px dashed color-mix(in srgb, var(--borderSecondary) 40%, transparent);
          border-radius: 4px; padding: 6px 16px; cursor: pointer;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px; font-weight: 500; letter-spacing: 0.08em;
          color: var(--accent); transition: border-color 0.15s;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .dd-add-row-btn:hover { border-color: color-mix(in srgb, var(--accent) 50%, transparent); }

        /* ── Animations ── */
        @keyframes dd-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dd-scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes dd-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        className="dd-overlay"
        onClick={(e) => { if (e.target === e.currentTarget && !loading) handleClose(); }}
      >
        <div className="dd-panel" onClick={(e) => e.stopPropagation()}>

          {/* ═══════ HEADER ═══════ */}
          <div className="dd-header">
            <div>
              <h2 className="dd-header-title">Multi Level Delivery</h2>
              <p className="dd-header-sub">Configure sequential drops for complex mission profiles.</p>
            </div>
            <button
              className="dd-close-btn"
              onClick={handleClose}
              disabled={loading && status !== 'error'}
            >
              <Icon name="close" size={20} />
            </button>
          </div>

          {/* ═══════ SCROLLABLE BODY ═══════ */}
          <div className="dd-body">

            {/* ── Section: Mission Identifier ── */}
            <section>
              <label className="dd-section-label">Mission Identifier</label>
              <input
                className="dd-underline-input"
                type="text"
                placeholder="e.g., South-Sector-Express-04"
                value={missionName}
                onChange={(e) => setMissionName(e.target.value)}
                disabled={loading}
              />
            </section>

            {/* ── Section: Point Coordinates ── */}
            <section>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <label className="dd-section-label" style={{ margin: 0 }}>Point Coordinates</label>
                {savedLocations.length > 0 && (
                  <select
                    className="dd-underline-input"
                    style={{ width: 'auto', minWidth: 210, padding: '6px 10px' }}
                    value=""
                    disabled={loading}
                    onChange={(e) => {
                      insertSavedLocation(savedLocations.find(l => l._id === e.target.value));
                      e.target.value = '';
                    }}
                  >
                    <option value="">+ Saved location…</option>
                    {savedLocations.map(loc => (
                      <option key={loc._id} value={loc._id}>{loc.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="dd-table-wrap">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th>SN</th>
                      <th>Longitude</th>
                      <th>Latitude</th>
                      <th>Customer Name</th>
                      <th style={{ textAlign: 'center' }}>Add Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc, index) => (
                      <tr key={index}>
                        <td>{String(index + 1).padStart(2, '0')}</td>
                        <td>
                          <input
                            className="dd-cell-input"
                            type="text"
                            inputMode="decimal"
                            value={loc.lon}
                            onChange={(e) => updateLocation(index, 'lon', e.target.value)}
                            placeholder="79.08821"
                            disabled={loading}
                          />
                        </td>
                        <td>
                          <input
                            className="dd-cell-input"
                            type="text"
                            inputMode="decimal"
                            value={loc.lat}
                            onChange={(e) => updateLocation(index, 'lat', e.target.value)}
                            placeholder="21.14580"
                            disabled={loading}
                          />
                        </td>
                        <td className="dd-td-name">
                          <input
                            className="dd-cell-input"
                            type="text"
                            value={loc.name || ''}
                            onChange={(e) => updateLocation(index, 'name', e.target.value)}
                            placeholder="Customer name"
                            disabled={loading}
                            style={{ color: 'var(--textPrimary)' }}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {locations.length > 1 ? (
                            <button
                              className="dd-delete-btn"
                              onClick={() => removeLocation(index)}
                              disabled={loading}
                              title="Remove this stop"
                              style={{ margin: '0 auto' }}
                            >
                              <Icon name="delete" size={18} />
                            </button>
                          ) : (
                            <button
                              className="dd-add-icon-btn"
                              onClick={addLocation}
                              disabled={loading || locations.length >= MAX_LOCATIONS}
                              title="Add location"
                              style={{ margin: '0 auto' }}
                            >
                              <Icon name="add_circle" size={20} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Add location row when more than 1 exists */}
                    {locations.length > 1 && locations.length < MAX_LOCATIONS && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', borderBottom: 'none', padding: '10px' }}>
                          <button
                            className="dd-add-row-btn"
                            onClick={addLocation}
                            disabled={loading}
                          >
                            <Icon name="add_circle" size={16} />
                            ADD LOCATION ({locations.length}/{MAX_LOCATIONS})
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ── Section: Delivery Sequence ── */}
            {validLocations.length > 0 && (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label className="dd-section-label" style={{ margin: 0 }}>
                    Delivery Sequence ({String(validLocations.length).padStart(2, '0')}/{String(MAX_LOCATIONS).padStart(2, '0')})
                  </label>
                  <span style={{
                    fontSize: '10px', fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 500, letterSpacing: '0.08em',
                    color: 'color-mix(in srgb, var(--textSecondary) 60%, transparent)', fontStyle: 'italic',
                  }}>
                    Flight priority order
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {locations.map((loc, index) => {
                    if (!loc.lat || !loc.lon) return null;
                    return (
                      <div key={index} className="dd-seq-card">
                        <span className="dd-drag-handle">
                          <Icon name="drag_indicator" size={20} />
                        </span>
                        <div className="dd-seq-badge">{String(index + 1).padStart(2, '0')}</div>
                        <div style={{ flex: 1 }}>
                          <p className="dd-seq-name">{loc.name || `Stop #${index + 1}`}</p>
                          <p className="dd-seq-meta">
                            Drop Zone · {parseFloat(loc.lat).toFixed(5)}, {parseFloat(loc.lon).toFixed(5)} · Alt {altitude}m
                          </p>
                        </div>
                        {locations.length > 1 && (
                          <button
                            className="dd-delete-btn"
                            onClick={() => removeLocation(index)}
                            disabled={loading}
                          >
                            <Icon name="delete" size={18} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Section: Flight Parameters ── */}
            <section>
              <label className="dd-section-label">Flight Parameters</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="dd-adv-label">Cruise Altitude (m)</label>
                  <input
                    className="dd-adv-input"
                    type="number"
                    value={altitude}
                    onChange={(e) => { setAltitude(e.target.value); setError(''); }}
                    disabled={loading}
                    min="1" max="500"
                  />
                </div>
                <div>
                  <label className="dd-adv-label">Drop Hover Height (m)</label>
                  <input
                    className="dd-adv-input"
                    type="number"
                    value={dropHeight}
                    onChange={(e) => { setDropHeight(e.target.value); setError(''); }}
                    disabled={loading}
                    min="1" max="100"
                    title="Height the drone holds while the winch lowers the parcel"
                  />
                </div>
                <div>
                  <label className="dd-adv-label">Nav Speed (m/s)</label>
                  <input
                    className="dd-adv-input"
                    type="number"
                    value={navSpeed}
                    onChange={(e) => { setNavSpeed(e.target.value); setError(''); }}
                    disabled={loading}
                    min="1" max="20" step="0.1"
                  />
                </div>
              </div>
            </section>

            {/* ── Section: Advanced Servo Settings ── */}
            <section>
              <button
                className="dd-adv-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
                disabled={loading}
              >
                <Icon name={showAdvanced ? 'expand_less' : 'expand_more'} size={18} />
                Winch & Drop Settings
              </button>

              {showAdvanced && (
                <div className="dd-adv-panel">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div>
                      <label className="dd-adv-label">Rope Length (m)</label>
                      <input
                        className="dd-adv-input"
                        type="number"
                        value={ropeLength}
                        onChange={(e) => { setRopeLength(e.target.value); setError(''); }}
                        disabled={loading}
                        min="1" max="100"
                        title="Rope paid out to lower the parcel to the ground"
                      />
                    </div>
                    <div>
                      <label className="dd-adv-label">Winch Rate (m/s)</label>
                      <input
                        className="dd-adv-input"
                        type="number"
                        value={winchRate}
                        onChange={(e) => { setWinchRate(e.target.value); setError(''); }}
                        disabled={loading}
                        min="0.1" max="5" step="0.1"
                        title="Speed the winch really runs at — not higher than the autopilot's WINCH_RATE_MAX. The drone waits rope ÷ rate before the hook opens, and again while the rope winds back in"
                      />
                    </div>
                    <div>
                      <label className="dd-adv-label">Settle Time (s)</label>
                      <input
                        className="dd-adv-input"
                        type="number"
                        value={loiterTime}
                        onChange={(e) => { setLoiterTime(e.target.value); setError(''); }}
                        disabled={loading}
                        min="1" max="60"
                        title="Extra hold before lowering, and again once the rope is out before the hook opens. The wait for the winch itself is added automatically"
                      />
                    </div>
                    <div>
                      <label className="dd-adv-label">Release Servo Channel</label>
                      <input
                        className="dd-adv-input"
                        type="number"
                        value={servoChannel}
                        onChange={(e) => { setServoChannel(e.target.value); setError(''); }}
                        disabled={loading}
                        min="1" max="16"
                      />
                    </div>
                    <div>
                      <label className="dd-adv-label">Release PWM (µs)</label>
                      <input
                        className="dd-adv-input"
                        type="number"
                        value={servoPWM}
                        onChange={(e) => { setServoPWM(e.target.value); setError(''); }}
                        disabled={loading}
                        min="800" max="2200"
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ── Section: Mission Feasibility ── */}
            {validLocations.length > 0 && (
              <section>
                <label className="dd-section-label">Mission Feasibility</label>
                <div className="dd-stats-grid">
                  <div className="dd-stat-card">
                    <p className="dd-stat-label">Total Distance (est.)</p>
                    <p className="dd-stat-value">{estDistance ? `${estDistance} km` : '--'}</p>
                  </div>
                  <div className="dd-stat-card">
                    <p className="dd-stat-label">Required Battery</p>
                    <p className="dd-stat-value">{estBattery}%</p>
                  </div>
                </div>
                {(estBattery > 70 || (estBatteryVerdict && !estBatteryVerdict.ok)) && (
                  <div className="dd-alert dd-alert-warning" style={{ marginTop: '12px' }}>
                    <Icon name="warning" size={20} style={{ color: '#ffb4ab' }} className="dd-alert-icon" />
                    <p className="dd-alert-text">Warning: Low battery margin for total mission distance.</p>
                  </div>
                )}
              </section>
            )}

            {/* ── Error Alert ── */}
            {error && (
              <div className="dd-alert dd-alert-warning">
                <Icon name="error" size={20} style={{ color: '#ffb4ab' }} className="dd-alert-icon" />
                <p className="dd-alert-text">{error}</p>
              </div>
            )}

            {/* ── Status Alert ── */}
            {status && status !== 'error' && (
              <div className={`dd-alert ${status === 'done' ? 'dd-alert-success' : 'dd-alert-info'}`}>
                {status !== 'done' && <span className="dd-spinner" />}
                {status === 'done' && <Icon name="check_circle" size={20} style={{ color: 'var(--success)' }} className="dd-alert-icon" />}
                <p className="dd-alert-text">{getStatusText()}</p>
              </div>
            )}

          </div>

          {/* ═══════ FOOTER ═══════ */}
          <div className="dd-footer">
            <div className="dd-footer-info">
              <Icon name="info" size={16} />
              <span>
                {validLocations.length > 0
                  ? `${3 + validLocations.length * 9} waypoints · Est. battery: ${estBattery}%`
                  : 'Add delivery locations to begin'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                className="dd-cancel-btn"
                onClick={handleClose}
                disabled={loading && status !== 'error'}
              >
                Cancel
              </button>
              <button
                className="dd-launch-btn"
                onClick={handleGenerate}
                disabled={loading || validLocations.length === 0 || !droneConnected}
              >
                {loading ? 'Processing…' : 'Launch Mission'}
                {!loading && <Icon name="rocket_launch" size={18} fill />}
              </button>
            </div>
          </div>

          {/* Drone not connected warning */}
          {!droneConnected && (
            <div className="dd-drone-warning">
              <Icon name="warning" size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              DRONE MUST BE CONNECTED TO LAUNCH DELIVERY MISSION
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default DeliveryDialog;
